/**
 * API 类型定义
 */

/** 包含 state 的响应 data 包装 */
export type ApiData<T = any> = T

// ============ 通用类型 ============

/** API 响应基础结构 */
export interface ApiResponse<T = any> {
  code: number
  message: string
  data: ApiData<T>
  total?: number
  /** 服务器时间戳 */
  server_ts: number
}

/** 短信用途枚举 */
export enum SmsPurpose {
  /** 登录 */
  Login = 1,
  /** 注册 */
  Register = 2,
  /** 绑定谷歌验证器(备用) */
  BindTotp = 5,
  /** 解绑谷歌验证器(备用) */
  UnBindTotp = 6,
  /** 重置密码 */
  ResetPassword = 7,
  /** 绑定手机号 */
  BindPhone = 8,
  /** 解绑手机号 */
  UnBindPhone = 9,
  /** 重置支付密码 */
  RecoverPIN = 10,
}

/** 邮件用途枚举 */
export enum EmailPurpose {
  /** 邮箱绑定 */
  EmailBind = 1,
  /** 找回密码 */
  EmailResetPassword = 2,
  /** 重置支付密码 */
  EmailResetPIN = 3,
}

/** 注册/登录类型枚举 */
export enum AuthType {
  /** 账号密码 */
  AccountPassword = 2,
  /** 手机验证码 */
  PhoneSms = 1,
  /** 手机号+密码 */
  PhonePwd = 3,
}

// ============ 请求参数类型 ============

/** 发送短信验证码请求 */
export interface SendSmsRequest {
  /** 短信用途：1=注册，2=登录，3=重置密码 */
  purpose: SmsPurpose
  /** 区号，如：+86，+852 */
  area: string
  /** 手机号码 */
  number: string
}

/** 账号密码注册请求 */
export interface RegisterByAccountRequest {
  /** 注册类型：1=账号密码 */
  type: AuthType.AccountPassword
  /** 用户账号，6-18位字母数字 */
  account: string
  /** 登录密码，8-20位包含字母+数字 */
  password: string
}

/** 手机号注册请求 */
export interface RegisterByPhoneRequest {
  /** 注册类型：1=手机号 */
  type: AuthType.PhoneSms
  /** 区号 */
  area_code: string
  /** 手机号 */
  phone: string
  /** 短信验证码 */
  sms_code: string
}

/** 手机或邮箱验证 */
export interface BindPhoneRequest {
  /** 区号 */
  area_code?: string
  /** 手机号 */
  phone?: string
  /** 短信验证码 */
  sms_code?: string

  email?: string
  email_code?: string
}

export interface AreaAndPhone {
  /** 区号 */
  area_code: string
  /** 手机号 */
  phone: string
}

/** 注册请求（联合类型） */
export type RegisterRequest = RegisterByAccountRequest | RegisterByPhoneRequest

/** 账号密码登录请求 */
export interface LoginByAccountRequest {
  /** 登录类型：2=账号密码 */
  type: AuthType.AccountPassword
  /** 用户账号或手机号 */
  account: string
  /** 登录密码 */
  password: string
  /** 设备ID（可选） */
  device_id?: string
}

/** 手机号+密码登录请求 */
export interface LoginByPhonePwdRequest {
  /** 登录类型：3=手机号+密码 */
  type: AuthType.PhonePwd
  /** 区号 */
  area_code: string
  /** 手机号 */
  phone: string
  /** 密码 */
  password: string

  /** 设备ID（可选） */
  device_id?: string
}

/** 登录请求（联合类型） */
export type LoginRequest = LoginByAccountRequest | LoginByPhonePwdRequest

/** 重置密码 - 步骤1 请求
 * 手机和邮箱二选一
 */
export interface ResetPasswordStep1Request {
  /** 区号 */
  area_code?: string
  /** 手机号 */
  phone?: string
  /** 短信验证码 */
  sms_code?: string
  email?: string
  email_code?: string
  /** 账号（可选，用于验证绑定关系） */
  account?: string
}

/** 重置密码 - 步骤2 请求 */
export interface ResetPasswordStep2Request {
  /** 步骤1返回的hash */
  hash: string
  /** 区号 */
  area_code?: string
  /** 手机号 */
  phone?: string

  email?: string
  /** 新密码，8-20位包含字母+数字 */
  password: string
}

// ============ 响应数据类型 ============

/** 用户信息 */
export interface UserInfo {
  /** 用户ID */
  id: number
  /** 昵称 */
  nickname: string
  /** 头像URL */
  avatar: string
  /** 语言 */
  lang: string
  /** 创建时间戳 */
  created_at: number
}

/** 发送短信响应 */
export interface SendSmsResponse {
  code: number
  msg: string
}

/** 注册响应 */
export interface RegisterResponse extends UserInfo {
  code: number
  msg: string
}

/** 登录响应 */
export interface LoginResponse extends UserInfo {
  code: number
  data: {
    account: string
    attempts: {
      left_attempts: number
      lock_left_secs: number
      max_attempts: number
    }
    avatar: string
    created_at: number
    id: number
    lang: string
    nickname: string
    role: string
    status: number
  }
  message: string
}

/** 重置密码步骤1响应 */
export interface ResetPasswordStep1Response {
  code: number
  msg: string
  /** hash用于步骤2，有效期8分钟 */
  hash: string
}

/** 重置密码步骤2响应 */
export interface ResetPasswordStep2Response {
  code: number
  msg: string
}

// ============ 错误码枚举 ============

/** API 错误码 */
export enum ApiErrorCode {
  /** 成功 */
  Success = 0,
  /** 手机号格式错误 */
  InvalidPhoneNumber = 10001,
  /** 区号格式错误 */
  InvalidAreaCode = 10002,
  /** 发送过于频繁 */
  SendTooFrequently = 10003,
  /** 账号已存在 */
  AccountAlreadyExists = 10004,
  /** 账号格式错误 */
  InvalidAccountFormat = 10005,
  /** 密码格式错误 */
  InvalidPasswordFormat = 10006,
  /** 验证码错误 */
  InvalidSmsCode = 10007,
  /** 密码错误 */
  WrongPassword = 10008,
  /** 账户已停用 */
  AccountFrozen = 10009,
  /** 登录冻结24小时 */
  LoginFrozen = 10010,
  /** 用户不存在 */
  UserNotFound = 10011,
  /** 手机号未注册 */
  PhoneNotFound = 10012,
  /** 账号与手机号不匹配 */
  WrongBindUser = 10013,
  /** hash已过期或无效 */
  RequestExpired = 10014,
}

/** 区号常量 */
export const AreaCodes = {
  /** 中国大陆 */
  CN: '+86',
  /** 中国香港 */
  HK: '+852',
  /** 中国台湾 */
  TW: '+886',
  /** 美国 */
  US: '+1',
  /** 日本 */
  JP: '+81',
  /** 韩国 */
  KR: '+82',
  /** 新加坡 */
  SG: '+65',
  /** 马来西亚 */
  MY: '+60',
  /** 泰国 */
  TH: '+66',
  /** 越南 */
  VN: '+84',
} as const

export type AreaCodeValue = (typeof AreaCodes)[keyof typeof AreaCodes]
